import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Plus, Search } from 'lucide-react';
import { api } from '../lib/api';
import { useOffers, offersKey } from '../hooks/useOffers';
import { formatDate, formatPrice, STATUS_CLASSES, statusLabel } from '../lib/format';
import { useI18n } from '../lib/i18n';
import type { OfferDto } from '@shared/types';

export default function Offers() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const { data: offers = [], isLoading } = useOffers();
  const [query, setQuery] = useState('');

  const create = useMutation({
    mutationFn: () => api.post<{ data: OfferDto }>('/offers', {}).then((r) => r.data),
    onSuccess: (offer) => {
      qc.invalidateQueries({ queryKey: offersKey });
      navigate(`/offers/${offer.id}`);
    },
  });

  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? offers.filter((o) =>
        [o.number, o.clientName, o.property].join(' ').toLowerCase().includes(needle),
      )
    : offers;

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-heading text-3xl font-bold text-dark">{t('offers.title')}</h1>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="input w-64 pl-9"
              placeholder={t('offers.search')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <button
            className="btn-primary"
            onClick={() => create.mutate()}
            disabled={create.isPending}
          >
            <Plus size={16} /> {create.isPending ? t('offers.creating') : t('offers.new')}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="card p-8 text-sm text-gray-500">{t('common.loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="card flex flex-col items-center gap-3 p-16 text-center">
          <FileText size={32} className="text-gray-300" />
          <div className="font-heading text-lg font-semibold text-dark">
            {offers.length === 0 ? t('offers.empty') : t('offers.notFound')}
          </div>
          <p className="max-w-sm text-sm text-gray-600">
            {offers.length === 0 ? t('offers.emptyHint') : t('offers.notFoundHint')}
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">{t('offers.colNumber')}</th>
                <th className="px-4 py-3 text-left font-semibold">{t('offers.colClient')}</th>
                <th className="px-4 py-3 text-left font-semibold">{t('offers.colProperty')}</th>
                <th className="px-4 py-3 text-left font-semibold">{t('offers.colDate')}</th>
                <th className="px-4 py-3 text-left font-semibold">{t('offers.colStatus')}</th>
                <th className="px-4 py-3 text-right font-semibold">{t('offers.colTotal')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr
                  key={o.id}
                  onClick={() => navigate(`/offers/${o.id}`)}
                  className="cursor-pointer border-t border-gray-100 transition-colors hover:bg-teal-light/40"
                >
                  <td className="px-4 py-3 font-medium">
                    {o.number}
                    {o.lang === 'en' && (
                      <span className="ml-2 text-xs uppercase text-gray-400">EN</span>
                    )}
                  </td>
                  <td className="px-4 py-3">{o.clientName || <span className="text-gray-400">—</span>}</td>
                  <td className="px-4 py-3 text-gray-600">{o.property}</td>
                  <td className="px-4 py-3 text-gray-600">{formatDate(o.offerDate, lang)}</td>
                  <td className="px-4 py-3">
                    <span className={`badge ${STATUS_CLASSES[o.status]}`}>
                      {statusLabel(t, o.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold">
                    {formatPrice(o.total, o.isFrom, o.lang)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
